import { useCallback, useEffect, useState } from 'react';
import { useSearchParams as useRouterSearchParams } from 'react-router-dom';

export interface SearchState {
  query?: string;
  statusFilter?: string;
  viewMode?: string;
  [key: string]: any;
}

export function useSearchParams(defaultState: SearchState = {}) {
  const [searchParams, setSearchParams] = useRouterSearchParams();
  const [state, setState] = useState<SearchState>(defaultState);

  // Base64 인코딩/디코딩 함수
  const encodeSearchState = useCallback((state: SearchState): string => {
    try {
      const jsonString = JSON.stringify(state);
      return btoa(encodeURIComponent(jsonString));
    } catch (error) {
      console.error('검색 상태 인코딩 실패:', error);
      return '';
    }
  }, []);

  const decodeSearchState = useCallback((encoded: string): SearchState => {
    try {
      const jsonString = decodeURIComponent(atob(encoded));
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('검색 상태 디코딩 실패:', error);
      return {};
    }
  }, []);

  // URL에서 검색 상태 복원
  useEffect(() => {
    const encodedState = searchParams.get('search');
    if (encodedState) {
      const decodedState = decodeSearchState(encodedState);
      setState(prev => ({ ...prev, ...decodedState }));
    }
  }, [searchParams, decodeSearchState]);

  // 검색 상태 업데이트 함수
  const updateSearchState = useCallback((updates: Partial<SearchState>) => {
    setState(prev => {
      const newState = { ...prev, ...updates };
      
      // 빈 값들 제거
      const filteredState: SearchState = {};
      Object.entries(newState).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          filteredState[key] = value;
        }
      });

      // URL 업데이트
      const encoded = encodeSearchState(filteredState);
      if (encoded && Object.keys(filteredState).length > 0) {
        setSearchParams({ search: encoded });
      } else {
        setSearchParams({});
      }

      return newState;
    });
  }, [encodeSearchState, setSearchParams]);

  // 검색 상태 초기화
  const clearSearchState = useCallback(() => {
    setState(defaultState);
    setSearchParams({});
  }, [defaultState, setSearchParams]);

  // 현재 검색 URL 가져오기
  const getSearchUrl = useCallback(() => {
    const currentUrl = new URL(window.location.href);
    return currentUrl.toString();
  }, []);

  // 검색 상태를 URL 파라미터로 공유
  const shareSearchState = useCallback((customState?: SearchState) => {
    const stateToShare = customState || state;
    const encoded = encodeSearchState(stateToShare);
    const url = new URL(window.location.href);
    
    if (encoded && Object.keys(stateToShare).length > 0) {
      url.searchParams.set('search', encoded);
    } else {
      url.searchParams.delete('search');
    }
    
    return url.toString();
  }, [state, encodeSearchState]);

  return {
    searchState: state,
    updateSearchState,
    clearSearchState,
    getSearchUrl,
    shareSearchState,
    hasActiveSearch: Object.keys(state).some(key => state[key] && state[key] !== ''),
  };
}
